/**
 * The curtain: what turns a row of damaged tiles into a wall.
 *
 * Walls have always been stored per tile — `map.wall`, `map.wallHp` — and that
 * is the right storage: a drag-line places them, pathing reads them every frame,
 * and a save writes them as one flat layer. What was missing is that nothing
 * ever asked how a tile *relates to the tiles beside it*. A wall was a damage
 * number on a square; it had no ends, no corners, no runs and no circuit.
 *
 * Everything in this file is a pure function of `map.wall` and `map.building`.
 * Nothing here is stored, hashed or saved, and that is deliberate: connectivity
 * is derivable in a handful of array reads, and a second copy of it would be one
 * more thing that can disagree with the tile layer after a breach.
 *
 * Three ideas, and the rest of the wall work is built on them:
 *
 *   **A run.** Curtain tiles connect eight ways, so a wall dragged on a diagonal
 *   is still one wall. `curtainRun` walks outward from a tile breadth-first,
 *   which is how a company spreads along the section it was told to hold rather
 *   than piling onto the one square that was clicked.
 *
 *   **An end.** A curtain tile with fewer than two continuations is a dangling
 *   end, and a dangling end is the *visible* form of the commonest mistake in a
 *   game like this: a ring that does not close. A closed loop has no ends at
 *   all, so "are there ends" and "is the circuit open" are the same question.
 *
 *   **A breach.** Not a timer and not a flag — a *hole*: open ground with
 *   curtain on both sides of it. That makes a breach permanent until somebody
 *   fills it in, which is what it should be, and it costs no state whatsoever.
 */

import { MAP_H, MAP_TILES, MAP_W } from '../constants.ts';
import { WallKind } from '../types.ts';
import type { TileMap } from '../map.ts';

/** A tower is a square block of this many tiles a side. */
export const TOWER_SIZE = 2;

/**
 * How many models a section of curtain will hold.
 *
 * One to a wall tile, deliberately. A twelve-strong company therefore covers
 * twelve tiles of frontage and *looks* like twelve tiles of frontage, which is
 * the whole point: a player has to be able to see that the northern face is
 * manned and the eastern face is three men and a gap. Piling four to a tile
 * would be cheaper to order and would read as a blob.
 *
 * A tower holds two to the tile — eight for a 2×2 — because a tower is supposed
 * to be where the line is *thick*. That is most of a company on one hard point,
 * which is what makes taking it the attacker's problem.
 */
export function tileCapacity(kind: number): number {
  if (kind === WallKind.Tower) return 2;
  if (kind === WallKind.None) return 0;
  return 1;
}

/** 8-way neighbour offsets. Order is fixed: it decides how a run is walked. */
const NX = [0, 1, 0, -1, 1, 1, -1, -1];
const NY = [-1, 0, 1, 0, -1, 1, 1, -1];

export function tileX(k: number): number {
  return k % MAP_W;
}

export function tileY(k: number): number {
  return (k / MAP_W) | 0;
}

/** Centre of a tile, in world units. */
export function tileCentreX(k: number): number {
  return (k % MAP_W) + 0.5;
}

export function tileCentreY(k: number): number {
  return ((k / MAP_W) | 0) + 0.5;
}

export function isCurtain(map: TileMap, k: number): boolean {
  return k >= 0 && k < MAP_TILES && map.wall[k] !== WallKind.None;
}

/** A bounded reverse walk of connected decks. Cache eviction never changes a route. */
const deckRoutes = new WeakMap<TileMap, { version: number; routes: Map<number, Map<number, number>> }>();
const MAX_DECK_TILES = 8192;
export function curtainRoute(map: TileMap, goal: number): ReadonlyMap<number, number> {
  let cache = deckRoutes.get(map);
  if (!cache || cache.version !== map.wallVersion) {
    cache = { version: map.wallVersion, routes: new Map() };
    deckRoutes.set(map, cache);
  }
  const old = cache.routes.get(goal);
  if (old) return old;
  const next = new Map<number, number>();
  if (isCurtain(map, goal)) {
    next.set(goal, goal);
    const queue = [goal];
    for (let head = 0; head < queue.length && queue.length < MAX_DECK_TILES; head++) {
      const k = queue[head], x = k % MAP_W, y = Math.floor(k / MAP_W);
      // Cardinal links keep feet on the deck around a corner, never across air.
      for (let d = 0; d < 4; d++) {
        if (queue.length >= MAX_DECK_TILES) break;
        const nx = x + NX[d], ny = y + NY[d];
        if (!map.inBounds(nx, ny)) continue;
        const n = map.idx(nx, ny);
        if (next.has(n) || !isCurtain(map, n)) continue;
        next.set(n, k); queue.push(n);
      }
    }
  }
  if (cache.routes.size >= 64) cache.routes.delete(cache.routes.keys().next().value!);
  cache.routes.set(goal, next);
  return next;
}

/**
 * The orthogonal link mask — 1 = north, 2 = east, 4 = south, 8 = west.
 *
 * The same numbering the renderer's auto-tile bank is baked against, so the
 * simulation and the picture cannot disagree about which way a corner turns.
 */
export function linkMask(map: TileMap, tx: number, ty: number): number {
  let m = 0;
  if (ty > 0 && isCurtain(map, map.idx(tx, ty - 1))) m |= 1;
  if (tx < MAP_W - 1 && isCurtain(map, map.idx(tx + 1, ty))) m |= 2;
  if (ty < MAP_H - 1 && isCurtain(map, map.idx(tx, ty + 1))) m |= 4;
  if (tx > 0 && isCurtain(map, map.idx(tx - 1, ty))) m |= 8;
  return m;
}

/**
 * How many ways this section of curtain carries on.
 *
 * A building counts, and so does the map edge. A wall that runs into the side of
 * a granary is *terminated*, not dangling — the line is continuous through the
 * building because nothing can walk through it either. Counting only wall tiles
 * would paint an amber warning on a join that is perfectly sound, which is worse
 * than not painting one at all.
 */
export function continuations(map: TileMap, tx: number, ty: number): number {
  let n = 0;
  for (let d = 0; d < 8; d++) {
    const x = tx + NX[d];
    const y = ty + NY[d];
    if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) {
      // The edge of the world is as good as a wall; nothing comes in that way.
      n++;
      continue;
    }
    const k = map.idx(x, y);
    if (isCurtain(map, k) || map.building[k] >= 0) n++;
  }
  return n;
}

/** A dangling end: the visible form of a circuit that does not close. */
export function isOpenEnd(map: TileMap, tx: number, ty: number): boolean {
  if (!isCurtain(map, map.idx(tx, ty))) return false;
  return continuations(map, tx, ty) < 2;
}

/**
 * A hole in the line.
 *
 * Open ground with curtain on opposite sides of it, or curtain on three sides.
 * That is exactly the shape a collapsed section leaves and exactly the shape a
 * player forgot to fill, and it needs no memory of what used to stand there.
 */
export function isBreach(map: TileMap, tx: number, ty: number): boolean {
  const here = map.idx(tx, ty);
  if (isCurtain(map, here)) return false;
  if (map.building[here] >= 0) return false;
  const n = ty > 0 && isCurtain(map, map.idx(tx, ty - 1));
  const s = ty < MAP_H - 1 && isCurtain(map, map.idx(tx, ty + 1));
  const e = tx < MAP_W - 1 && isCurtain(map, map.idx(tx + 1, ty));
  const wst = tx > 0 && isCurtain(map, map.idx(tx - 1, ty));
  if (n && s) return true;
  if (e && wst) return true;
  return (n ? 1 : 0) + (s ? 1 : 0) + (e ? 1 : 0) + (wst ? 1 : 0) >= 3;
}

/**
 * Can a man on the ground get at a man standing on *this* section?
 *
 * A gate is a door, so yes. A section beside a hole in the line is climbable
 * over the spill of its own rubble, so yes. Anywhere else on an intact curtain,
 * no — and that "no" is the whole mechanic. It is what makes a breach the place
 * a siege is decided rather than one more square with less health than the rest.
 */
export function climbable(map: TileMap, k: number): boolean {
  if (map.wall[k] === WallKind.Gate) return true;
  const tx = k % MAP_W;
  const ty = (k / MAP_W) | 0;
  for (let d = 0; d < 8; d++) {
    const x = tx + NX[d];
    const y = ty + NY[d];
    if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
    if (isBreach(map, x, y)) return true;
  }
  return false;
}

/**
 * Walk the curtain outward from `start`, breadth-first, up to `max` tiles.
 *
 * Breadth-first rather than "follow the line in one direction" because a run
 * forks: a tower bulges out of it, a spur runs off toward the Hearth, two faces
 * meet at a corner. Spreading evenly from the tile that was clicked puts the
 * middle of the company where the player pointed and the flanks either side of
 * it, which is what pointing at a wall means.
 *
 * The visit order is a pure function of the map — the frontier is a plain queue
 * and ties inside a ring break on the fixed neighbour order above — so two runs
 * of the same seed assign the same men to the same stones.
 */
/**
 * The walk's visited set, allocated once and always handed back clean.
 *
 * A hundred and forty-seven thousand bytes is not worth allocating per order,
 * and it is not worth clearing wholesale either — the walk unmarks exactly the
 * tiles it marked before it returns, so the array is empty again by the time
 * anyone else asks for it. The tick is single-threaded and the walk does not
 * re-enter, which is what makes that safe.
 */
let runSeen: Uint8Array | null = null;

function runScratch(): Uint8Array {
  if (runSeen === null) runSeen = new Uint8Array(MAP_TILES);
  return runSeen;
}

export function curtainRun(map: TileMap, start: number, max: number): number[] {
  const seen = runScratch();
  const out: number[] = [];
  if (max <= 0 || !isCurtain(map, start)) return out;
  const queue: number[] = [start];
  seen[start] = 1;
  const touched: number[] = [start];
  let head = 0;
  while (head < queue.length && out.length < max) {
    const k = queue[head++];
    out.push(k);
    const tx = k % MAP_W;
    const ty = (k / MAP_W) | 0;
    for (let d = 0; d < 4; d++) {
      const x = tx + NX[d];
      const y = ty + NY[d];
      if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
      const n = map.idx(x, y);
      if (seen[n] === 1) continue;
      if (!isCurtain(map, n)) continue;
      seen[n] = 1;
      touched.push(n);
      queue.push(n);
    }
  }
  for (const k of touched) seen[k] = 0;
  return out;
}

/** The nearest curtain tile to (tx, ty) within `r`, or −1. Ties break low-index. */
export function nearestCurtain(map: TileMap, tx: number, ty: number, r: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let y = ty - r; y <= ty + r; y++) {
    if (y < 0 || y >= MAP_H) continue;
    for (let x = tx - r; x <= tx + r; x++) {
      if (x < 0 || x >= MAP_W) continue;
      const k = map.idx(x, y);
      if (!isCurtain(map, k)) continue;
      const d = (x - tx) * (x - tx) + (y - ty) * (y - ty);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
  }
  return best;
}

/**
 * The tile a company musters on to reach a given section: the neighbour of it
 * that a defender can actually stand on, nearest the Hearth.
 *
 * Nearest the Hearth rather than nearest the company, because a wall has an
 * inside and an outside and the inside is where the ladder is. Marching a
 * company to the *outer* face of its own wall would put it in front of the
 * horde, which is a funny bug exactly once.
 */
export function insideFoot(map: TileMap, k: number, passable?: (x: number, y: number) => boolean): { x: number; y: number } {
  const tx = k % MAP_W;
  const ty = (k / MAP_W) | 0;
  let bx = tx + 0.5;
  let by = ty + 0.5;
  let bestD = Infinity;
  // Stairs meet a face, never a corner. A diagonal foot requires travelling
  // sqrt(1/2) before entering the deck and can reverse an unfinished climb.
  for (let d = 0; d < 4; d++) {
    const x = tx + NX[d];
    const y = ty + NY[d];
    if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
    const n = map.idx(x, y);
    if (isCurtain(map, n) || map.building[n] >= 0) continue;
    if (passable && !passable(x, y)) continue;
    const dd = (x - map.hearthX) * (x - map.hearthX) + (y - map.hearthY) * (y - map.hearthY);
    if (dd < bestD) {
      bestD = dd;
      bx = x + 0.5;
      by = y + 0.5;
    }
  }
  return { x: bx, y: by };
}

// ── Reading the whole circuit ──────────────────────────────────────────────

/**
 * Is the settlement actually *inside* anything, and where does the line fail?
 *
 * This is the answer to the one question a wall-building screen has to be able
 * to answer at a glance and never could: **is my ring closed?** It is asked by
 * flooding the map inward from the edge over everything a besieger could walk
 * on, and seeing whether the flood reaches the Hearth. Gates count as shut,
 * because to an attacker they are.
 *
 * Derived, and read by the renderer alone. It is recomputed only when the wall
 * layer actually changes — `TileMap.wallVersion` — and the flood is bounded to
 * the walls' own bounding box plus a margin, because everything outside that box
 * is trivially outside the walls. On a settlement with a ring a hundred tiles
 * across that is ten thousand tiles rather than a hundred and forty-seven
 * thousand.
 */
export interface CurtainReading {
  /** True if the flood from the map edge cannot reach the Hearth. */
  sealed: boolean;
  /** Tiles where the curtain simply stops. Empty on a closed circuit. */
  ends: number[];
  /** Holes in the line — collapsed sections and gaps never filled. */
  breaches: number[];
  /** How many tiles of curtain are standing at all. */
  tiles: number;
}

export class CurtainWatch {
  readonly reading: CurtainReading = { sealed: false, ends: [], breaches: [], tiles: 0 };
  private stamp = -1;
  private flood: Uint8Array | null = null;

  /** Recompute if the wall layer has moved since last time. Cheap when it has not. */
  update(map: TileMap): CurtainReading {
    if (map.wallVersion === this.stamp) return this.reading;
    this.stamp = map.wallVersion;
    this.recompute(map);
    return this.reading;
  }

  private recompute(map: TileMap): void {
    const r = this.reading;
    r.ends.length = 0;
    r.breaches.length = 0;
    r.tiles = 0;
    r.sealed = false;

    let x0 = MAP_W;
    let y0 = MAP_H;
    let x1 = -1;
    let y1 = -1;
    for (let k = 0; k < MAP_TILES; k++) {
      if (map.wall[k] === WallKind.None) continue;
      r.tiles++;
      const x = k % MAP_W;
      const y = (k / MAP_W) | 0;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
    if (r.tiles === 0) return;

    x0 = Math.max(0, x0 - 1);
    y0 = Math.max(0, y0 - 1);
    x1 = Math.min(MAP_W - 1, x1 + 1);
    y1 = Math.min(MAP_H - 1, y1 + 1);

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const k = map.idx(x, y);
        if (map.wall[k] !== WallKind.None) {
          if (isOpenEnd(map, x, y)) r.ends.push(k);
        } else if (isBreach(map, x, y)) {
          r.breaches.push(k);
        }
      }
    }

    // The Hearth outside the walls' own bounding box cannot possibly be inside
    // them, and the flood below would never reach it to say so.
    const hx = map.hearthX;
    const hy = map.hearthY;
    if (hx < x0 || hx > x1 || hy < y0 || hy > y1) return;

    /**
     * The probe is the first walkable tile *beside* the Hearth, not the Hearth
     * tile itself — which is occupied by the Hearth, and therefore is never
     * reached by a flood that treats buildings as solid. Testing it directly
     * reported every settlement as sealed, including one with no walls at all.
     */
    let probe = -1;
    for (let rad = 1; rad <= 8 && probe < 0; rad++) {
      for (let dy = -rad; dy <= rad && probe < 0; dy++) {
        for (let dx = -rad; dx <= rad; dx++) {
          if (Math.abs(dx) !== rad && Math.abs(dy) !== rad) continue;
          const px = hx + dx;
          const py = hy + dy;
          if (px < x0 || py < y0 || px > x1 || py > y1) continue;
          const pk = map.idx(px, py);
          if (map.wall[pk] !== WallKind.None || map.building[pk] >= 0) continue;
          probe = pk;
          break;
        }
      }
    }
    // Nowhere to stand next to the Hearth at all. Nothing is getting in either.
    if (probe < 0) {
      r.sealed = true;
      return;
    }

    if (this.flood === null) this.flood = new Uint8Array(MAP_TILES);
    const seen = this.flood;
    const queue: number[] = [];
    const push = (x: number, y: number) => {
      const k = map.idx(x, y);
      if (seen[k] === 1) return;
      if (map.wall[k] !== WallKind.None) return;
      if (map.building[k] >= 0) return;
      seen[k] = 1;
      queue.push(k);
    };
    // Clear only the window we are about to use.
    for (let y = y0; y <= y1; y++) seen.fill(0, map.idx(x0, y), map.idx(x1, y) + 1);
    for (let x = x0; x <= x1; x++) {
      push(x, y0);
      push(x, y1);
    }
    for (let y = y0; y <= y1; y++) {
      push(x0, y);
      push(x1, y);
    }

    let head = 0;
    while (head < queue.length) {
      const k = queue[head++];
      const x = k % MAP_W;
      const y = (k / MAP_W) | 0;
      for (let d = 0; d < 8; d++) {
        const nx = x + NX[d];
        const ny = y + NY[d];
        if (nx < x0 || ny < y0 || nx > x1 || ny > y1) continue;
        push(nx, ny);
      }
    }

    r.sealed = seen[probe] === 0;
  }
}
